import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { AlertTriangle, Boxes, Coins, Package, Pencil, Plus, PlusCircle, Search, Trash2, TrendingDown } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { StatTile } from '@/components/StatTile';
import { EmptyState } from '@/components/EmptyState';
import { Pagination } from '@/components/Pagination';
import { formatMetaInsightsValue } from '@/utils/currency';
import { useLabels } from '@/contexts/WorkspaceContext';
import { useAuth } from '@/hooks/useAuth';
import {
  stockService, type StockItem, type StockSummary, type StockCategoryRow,
} from '@/services/stock.service';
import StockItemModal from './StockItemModal';
import RecordMovementModal from './RecordMovementModal';
import StockItemDetailModal from './StockItemDetailModal';

const LIMIT = 25;

export default function StockPage() {
  const t = useLabels();
  const { user } = useAuth();
  const isAdmin = user?.role === 'company_admin';

  const [summary, setSummary] = useState<StockSummary | null>(null);
  const [categories, setCategories] = useState<StockCategoryRow[]>([]);
  const [items, setItems] = useState<StockItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [lowOnly, setLowOnly] = useState(false);

  const [editing, setEditing] = useState<StockItem | 'new' | null>(null);
  const [movementTarget, setMovementTarget] = useState<StockItem | null>(null);
  const [detailUid, setDetailUid] = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    try { setSummary(await stockService.summary()); } catch { /* interceptor toasts */ }
  }, []);
  const loadCategories = useCallback(async () => {
    try { setCategories((await stockService.categories()).items); } catch { /* interceptor toasts */ }
  }, []);
  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await stockService.listItems({
        q: q.trim() || undefined,
        category: category || undefined,
        lowOnly: lowOnly ? 'true' : undefined,
        page, limit: LIMIT,
      });
      setItems(r.items); setTotal(r.total);
    } catch { /* interceptor toasts */ } finally { setLoading(false); }
  }, [q, category, lowOnly, page]);

  useEffect(() => { void loadSummary(); void loadCategories(); }, [loadSummary, loadCategories]);
  useEffect(() => { void loadItems(); }, [loadItems]);

  const refreshAll = () => { void loadItems(); void loadSummary(); void loadCategories(); };

  const removeItem = async (item: StockItem) => {
    if (!window.confirm(`Delete "${item.name}"? Its movement history is kept.`)) return;
    try { await stockService.removeItem(item.stockUid); toast.success('Item deleted'); refreshAll(); }
    catch (e: any) { if (!e?.response) toast.error('Could not delete this item.'); }
  };

  const currency = summary?.currency ?? 'USD';

  return (
    <div>
      <Topbar
        title={t('stock')}
        breadcrumb={`${t('operations')} / ${t('stock')}`}
        description="What's on hand, what's low, and every movement in between."
        right={
          <Button size="sm" iconLeft={<Plus size={14} />} onClick={() => setEditing('new')}>
            Add {t('stock', 'singular').toLowerCase()}
          </Button>
        }
      />

      <div className="w-full px-4 py-6 sm:px-7 sm:py-7 space-y-5">
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <StatTile label="Items" value={summary ? summary.items : '—'} icon={<Boxes size={16} />} accent="gold" />
          <StatTile label="Low stock" value={summary ? summary.lowStockCount : '—'} icon={<AlertTriangle size={16} />} accent="red" />
          <StatTile label="Stock value" value={summary ? formatMetaInsightsValue(String(summary.stockValue), currency) : '—'} icon={<Coins size={16} />} accent="green" />
          <StatTile label="Waste (30d)" value={summary ? formatMetaInsightsValue(String(summary.wasteValueLast30Days), currency) : '—'} icon={<TrendingDown size={16} />} accent="amber" />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-[200px] flex-1 sm:flex-none sm:w-72">
            <Input
              iconLeft={<Search size={14} />}
              placeholder={`Search ${t('stock', 'plural').toLowerCase()}…`}
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              className="!h-9 !text-xs"
            />
          </div>
          <Select value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }} className="!h-9 !w-auto !text-xs">
            <option value="">All categories</option>
            {categories.map((c) => <option key={c.category} value={c.category}>{c.category} ({c.count})</option>)}
          </Select>
          <button
            onClick={() => { setLowOnly((v) => !v); setPage(1); }}
            className={clsx(
              'inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold whitespace-nowrap transition',
              lowOnly ? 'border-danger bg-danger-soft text-danger' : 'border-border bg-surface text-text-secondary hover:bg-surface-alt',
            )}
          >
            <AlertTriangle size={12} />
            Low stock only
          </button>
        </div>

        {loading && items.length === 0 ? (
          <Card padded={false} className="overflow-hidden">
            <div className="divide-y divide-border">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-5 py-4">
                  <div className="flex-1 space-y-1.5"><div className="skeleton h-4 w-48 rounded" /><div className="skeleton h-3 w-28 rounded" /></div>
                  <div className="skeleton h-5 w-16 rounded-full" />
                  <div className="skeleton h-5 w-16 rounded-full" />
                </div>
              ))}
            </div>
          </Card>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Package size={26} />}
            title={`No ${t('stock', 'plural').toLowerCase()} yet`}
            description={`Add your first ${t('stock', 'singular').toLowerCase()} to start tracking quantities and cost.`}
            action={<Button onClick={() => setEditing('new')} iconLeft={<Plus size={14} />}>Add {t('stock', 'singular').toLowerCase()}</Button>}
          />
        ) : (
          <>
            {/* Desktop table */}
            <Card padded={false} className="hidden overflow-hidden xl:block">
              <div className="grid grid-cols-[1fr_140px_140px_140px_110px] gap-3 border-b border-border bg-surface-alt/40 px-5 py-2.5 text-2xs font-semibold uppercase tracking-wide text-text-muted">
                <span>Item</span><span>Category</span><span>Quantity</span><span>Value</span><span>Actions</span>
              </div>
              <div className="divide-y divide-border">
                {items.map((it) => (
                  <div key={it.stockUid} className="grid grid-cols-[1fr_140px_140px_140px_110px] items-center gap-3 px-5 py-3 text-sm">
                    <button onClick={() => setDetailUid(it.stockUid)} className="min-w-0 text-left">
                      <div className="truncate font-medium text-text-primary hover:text-primary">{it.name}</div>
                      <div className="truncate text-2xs text-text-muted">{it.sku ? `SKU ${it.sku}` : '—'}</div>
                    </button>
                    <span className="truncate text-xs text-text-secondary">{it.category || '—'}</span>
                    <span className="flex items-center gap-1.5 text-xs">
                      <span className={clsx('font-medium', it.low ? 'text-danger' : 'text-text-primary')}>{it.quantity} {it.unit}</span>
                      {it.low && <span className="rounded-full bg-danger-soft px-1.5 py-0.5 text-[10px] font-semibold text-danger">Low</span>}
                    </span>
                    <span className="text-xs text-text-secondary">{formatMetaInsightsValue(String(it.value), it.currency ?? currency)}</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setMovementTarget(it)} className="flex h-7 w-7 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-primary" aria-label="Record movement" title="Record movement">
                        <PlusCircle size={14} />
                      </button>
                      <button onClick={() => setEditing(it)} className="flex h-7 w-7 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary" aria-label="Edit">
                        <Pencil size={13} />
                      </button>
                      {isAdmin && (
                        <button onClick={() => removeItem(it)} className="flex h-7 w-7 items-center justify-center rounded-lg text-text-muted hover:bg-danger-soft hover:text-danger" aria-label="Delete">
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Mobile cards */}
            <div className="space-y-2.5 xl:hidden">
              {items.map((it) => (
                <Card key={it.stockUid} className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <button onClick={() => setDetailUid(it.stockUid)} className="min-w-0 text-left">
                      <div className="truncate font-medium text-text-primary">{it.name}</div>
                      <div className="truncate text-2xs text-text-muted">{it.category || 'Uncategorised'}{it.sku ? ` · SKU ${it.sku}` : ''}</div>
                    </button>
                    <div className="flex shrink-0 gap-1">
                      <button onClick={() => setMovementTarget(it)} className="flex h-7 w-7 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-primary" aria-label="Record movement">
                        <PlusCircle size={14} />
                      </button>
                      <button onClick={() => setEditing(it)} className="flex h-7 w-7 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary" aria-label="Edit">
                        <Pencil size={13} />
                      </button>
                      {isAdmin && (
                        <button onClick={() => removeItem(it)} className="flex h-7 w-7 items-center justify-center rounded-lg text-text-muted hover:bg-danger-soft hover:text-danger" aria-label="Delete">
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                    <span className={clsx('inline-flex items-center rounded-full px-2.5 py-0.5 text-2xs font-semibold', it.low ? 'bg-danger-soft text-danger' : 'bg-surface-alt text-text-secondary')}>
                      {it.quantity} {it.unit}{it.low ? ' · Low' : ''}
                    </span>
                    <span className="inline-flex items-center rounded-full bg-surface-alt px-2.5 py-0.5 text-2xs font-semibold text-text-secondary">
                      {formatMetaInsightsValue(String(it.value), it.currency ?? currency)}
                    </span>
                  </div>
                </Card>
              ))}
            </div>

            {total > LIMIT && <Pagination page={page} total={total} limit={LIMIT} onChange={setPage} />}
          </>
        )}
      </div>

      <StockItemModal
        open={!!editing}
        item={editing === 'new' ? null : editing}
        categories={categories.map((c) => c.category)}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); refreshAll(); }}
      />
      <RecordMovementModal
        open={!!movementTarget}
        item={movementTarget}
        isAdmin={isAdmin}
        onClose={() => setMovementTarget(null)}
        onRecorded={() => { setMovementTarget(null); refreshAll(); }}
      />
      <StockItemDetailModal
        open={!!detailUid}
        stockUid={detailUid}
        onClose={() => setDetailUid(null)}
        onEdit={(item) => { setDetailUid(null); setEditing(item); }}
        onRecordMovement={(item) => { setDetailUid(null); setMovementTarget(item); }}
      />
    </div>
  );
}
